import { Loader2, Search, Settings2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import type { ChatSessionMeta } from '@/components/talk/types';
import { formatElapsed } from './utils';

interface ThreadsPanelProps {
  chatSessionsLoading: boolean;
  chatSessions: ChatSessionMeta[];
  filteredChatSessions: ChatSessionMeta[];
  visibleChatSessions: ChatSessionMeta[];
  activeChatSessionId: string | null;
  actionLoading: string | null;
  threadSearch: string;
  renamingSessionId: string | null;
  renamingValue: string;
  selectedThreadIds: string[];
  onThreadSearchChange: (value: string) => void;
  onActivate: (chatSessionId: string) => void;
  onDelete: (chatSessionId: string) => void;
  onToggleSelected: (chatSessionId: string) => void;
  onToggleSelectAllVisible: () => void;
  onStartRename: (chatSession: ChatSessionMeta) => void;
  onRenamingValueChange: (value: string) => void;
  onCommitRename: (chatSession: ChatSessionMeta) => void;
  onCancelRename: () => void;
  onLoadMore: () => void;
}

export function ThreadsPanel({
  chatSessionsLoading,
  chatSessions,
  filteredChatSessions,
  visibleChatSessions,
  activeChatSessionId,
  actionLoading,
  threadSearch,
  renamingSessionId,
  renamingValue,
  selectedThreadIds,
  onThreadSearchChange,
  onActivate,
  onDelete,
  onToggleSelected,
  onToggleSelectAllVisible,
  onStartRename,
  onRenamingValueChange,
  onCommitRename,
  onCancelRename,
  onLoadMore,
}: ThreadsPanelProps) {
  const selectedVisibleCount = visibleChatSessions.filter((item) =>
    selectedThreadIds.includes(item.id),
  ).length;
  const allVisibleSelected =
    visibleChatSessions.length > 0 &&
    selectedVisibleCount === visibleChatSessions.length;

  return (
    <div className="px-1">
      <div className="sticky top-0 z-10 -mx-1 border-b bg-background/95 px-1 pb-3 pt-1 backdrop-blur supports-[backdrop-filter]:bg-background/85">
        {visibleChatSessions.length > 0 && selectedThreadIds.length > 0 ? (
          <div className="mb-3 flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={allVisibleSelected}
                onCheckedChange={() => onToggleSelectAllVisible()}
                aria-label="Select all visible threads"
              />
              <span>{selectedThreadIds.length} selected</span>
            </label>
          </div>
        ) : null}
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={threadSearch}
            onChange={(event) => onThreadSearchChange(event.target.value)}
            placeholder="Search threads..."
            className="h-10 rounded-full pl-9"
          />
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {chatSessionsLoading ? (
          <>
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
          </>
        ) : chatSessions.length === 0 ? (
          <div className="rounded-xl border border-dashed px-4 py-6 text-sm text-muted-foreground">
            No PM chats yet. Create one to start a new conversation thread.
          </div>
        ) : filteredChatSessions.length === 0 ? (
          <div className="rounded-xl border border-dashed px-4 py-6 text-sm text-muted-foreground">
            No threads match "{threadSearch.trim()}".
          </div>
        ) : (
          visibleChatSessions.map((chatSession) => {
            const isActive = chatSession.id === activeChatSessionId;
            const isBusy =
              actionLoading === `activate:${chatSession.id}` ||
              actionLoading === `delete:${chatSession.id}` ||
              actionLoading === `rename:${chatSession.id}`;
            return (
              <div
                key={chatSession.id}
                className={`rounded-lg border px-3 py-2.5 transition-colors ${isActive ? 'border-primary/50 bg-primary/5' : 'bg-background hover:border-primary/30'}`}
              >
                <div className="flex items-start justify-between gap-2.5">
                  <div className="pt-0.5">
                    <Checkbox
                      checked={selectedThreadIds.includes(chatSession.id)}
                      onCheckedChange={() => onToggleSelected(chatSession.id)}
                      aria-label={`Select thread ${chatSession.name}`}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    {renamingSessionId === chatSession.id ? (
                      <Input
                        value={renamingValue}
                        autoFocus
                        onChange={(event) =>
                          onRenamingValueChange(event.target.value)
                        }
                        onBlur={() => onCommitRename(chatSession)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter')
                            onCommitRename(chatSession);
                          if (event.key === 'Escape') onCancelRename();
                        }}
                        className="h-8"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => onActivate(chatSession.id)}
                        className="w-full text-left"
                      >
                        <p className="truncate text-sm font-medium">
                          {chatSession.name}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {chatSession.messageCount} msgs · updated{' '}
                          {formatElapsed(chatSession.updatedAt)}
                        </p>
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onStartRename(chatSession)}
                      disabled={isBusy}
                      title="Rename"
                    >
                      <Settings2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => onDelete(chatSession.id)}
                      disabled={isBusy || chatSessions.length <= 1}
                      title="Delete"
                    >
                      {actionLoading === `delete:${chatSession.id}` ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {!chatSessionsLoading &&
      filteredChatSessions.length > visibleChatSessions.length ? (
        <div className="mt-4 flex justify-center">
          <Button variant="outline" size="sm" onClick={onLoadMore}>
            Load more
          </Button>
        </div>
      ) : null}
    </div>
  );
}
